import { Box, Skeleton, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Fragment } from "react";
import { orderBy } from "lodash";
import { differenceInMinutes } from "date-fns";
import ProviderTypeChip from "./ProviderTypeChip";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { GetVisitDetailsQuery } from "~/graphql/__generated__/graphql";
import { PrecautionConfig, useOrgConfig } from "~/providers/OrgConfigProvider";
import PrecautionOctagon from "~/components/patient/PrecautionOctagon";
import VisitErrandResources from "./VisitErrandResources";

type DeepPartial<T> = T extends object ? {
    [P in keyof T]?: DeepPartial<T[P]>;
} : T;

type VisitDetailsTabProps = {
  visit: DeepPartial<NonNullable<GetVisitDetailsQuery["visitSingle"]>>;
  loading?: boolean;
  showErrands?: boolean;
};

// Used in VisitRequestPopup and VisitPopup components for displaying visit details
const VisitDetailsTab = ({ visit, loading = false, showErrands = false }: VisitDetailsTabProps) => {
  const { visitTypes, precautions: precautionCatalog } = useOrgConfig();
  const { timeLabelWithZone } = useZonedDateFormat();
  const visitServices = visit.visitServices ?? [];

  const shouldDisplayArrivalWindow = visit.arrivalWindowStartTime &&
    visit.arrivalWindowEndTime && 
    new Date(visit.arrivalWindowStartTime).getTime() !== new Date(visit.arrivalWindowEndTime).getTime();

  const precautions = (visit.episode?.precautions ?? [])
    .map((p) => (p?.precautionId != null ? precautionCatalog[p.precautionId] : null))
    .filter((precaution): precaution is PrecautionConfig => precaution != null);
  const normalVisitResources = visit.visitResources?.filter(resource => !resource?.errandStatus) ?? [];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {visitTypes[visit.visitTypeId || ""]?.name}
      </Typography>

      {precautions.length > 0 && (
        <>
          <Typography variant="button">Precautions</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ mb: 2, mt: 0.5 }}>
            {precautions.map((precaution) => (
              <Box key={precaution.id} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                <PrecautionOctagon color={precaution.color} size={16} />
                <Box component="span" sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
                  {precaution.name}
                </Box>
              </Box>
            ))}
          </Stack>
        </>
      )}

      {visit.cancellationReason && (
        <>
          <Typography variant="button">Cancellation Reason</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {visit.cancellationReason.description}
          </Typography>
        </>
      )}

      {shouldDisplayArrivalWindow && (
        <>
          <Typography variant="button">Arrival Window</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {timeLabelWithZone(
              visit.arrivalWindowStartTime || "", false, visit.addressTimezone
            )}
            -
            {timeLabelWithZone(
              visit.arrivalWindowEndTime || "", false, visit.addressTimezone
            )}
          </Typography>
        </>
      )}

      {(normalVisitResources.length > 0 || loading) && (
        <Grid container direction="row" sx={{ mb: 2 }} alignItems="center">
          <Grid size={{ xs: 7 }}>
            <Typography variant="button">Provider</Typography>
          </Grid>
          <Grid size={{ xs: 5 }}>
            <Typography variant="button">Target Time</Typography>
          </Grid>

          {normalVisitResources.length > 0 && orderBy(normalVisitResources, "startTime").map((visitResource, idx) => {
            const provider = visitResource?.provider;
            const providerName = [provider?.firstName, provider?.lastName].filter(Boolean).join(" ");

            return (
              <Fragment key={visitResource?.id || idx}>
                <Grid size={{ xs: 7 }}>
                  {providerName ? (
                    <>
                      {provider?.firstName} {provider?.lastName}{" "}
                      {provider?.providerType?.name ? (
                        <ProviderTypeChip providerTypeName={provider.providerType.name} />
                      ) : loading ? (
                        <Skeleton
                          variant="rounded"
                          width={110}
                          height={22}
                          sx={{
                            display: "inline-block",
                            verticalAlign: "middle",
                            ml: 1,
                            borderRadius: "40px",
                          }}
                        />
                      ) : null}
                    </>
                  ) : loading ? (
                    <Skeleton variant="text" height={24} width="70%" />
                  ) : null}
                </Grid>
                <Grid size={{ xs: 5 }}>
                  {visitResource?.startTime && visitResource?.endTime ? (
                    <Typography variant="body1">
                      {timeLabelWithZone(
                        visitResource.startTime, true, visit.addressTimezone
                      )}

                      {" "}({differenceInMinutes(
                        new Date(visitResource.endTime),
                        new Date(visitResource.startTime)
                      )} min)
                    </Typography>
                  ) : loading ? (
                    <Skeleton
                      variant="rounded"
                      width={160}
                      height={22}
                      sx={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        borderRadius: "40px",
                      }}
                    />
                  ) : null}
                </Grid>
              </Fragment>
            );
          })}

          {loading && normalVisitResources.length === 0 && (
            <>
              <Grid size={{ xs: 7 }}>
                <Skeleton variant="text" height={24} width="70%" />
              </Grid>
              <Grid size={{ xs: 5 }}>
                <Skeleton
                  variant="rounded"
                  width={160}
                  height={22}
                  sx={{
                    display: "inline-block",
                    verticalAlign: "middle",
                    borderRadius: "40px",
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      )}

      {showErrands && visit.id && (
        <VisitErrandResources visit={visit as NonNullable<GetVisitDetailsQuery["visitSingle"]>} />
      )}

      <Typography variant="button">Patient Service Instructions</Typography>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        {visit.serviceInstructions || "None provided."}
      </Typography>

      {visit.addressStreet && (
        <>
          <Typography variant="button">Address</Typography>
          <Typography variant="subtitle1">{visit.addressStreet}</Typography>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {visit.addressCity} {visit.addressState}, {visit.addressZip}
          </Typography>
        </>
      )}

      <Typography variant="button">Services</Typography>
      {visitServices.length > 0 ? (
        <ul style={{ margin: 0 }}>
          {visitServices.map((visitService, idx) => (
            <li key={visitService?.id || idx}>
              <Typography variant="body1">{visitService?.service?.name}</Typography>
            </li>
          ))}
        </ul>
      ) : loading ? (
        <Skeleton variant="text" height={48} width={120} />
      ) : (
        <Typography variant="body1">No services.</Typography>
      )}
    </Box>
  );
};

export default VisitDetailsTab; 
